/**
 * Reads `?source=` and `?campaign=` URL params and (optionally) scrolls to +
 * highlights the matching homepage section. Used to convert Instagram /
 * TikTok / ManyChat traffic without redirecting to a separate landing page.
 */
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { trackEvent } from "@/lib/retention";

const CAMPAIGN_TARGETS = {
  affiliate: "home-affiliate",
  supplies:  "home-supplies",
  drop:      "home-seasonal",
};

export default function useCampaign() {
  const [params] = useSearchParams();
  const source   = params.get("source");
  const campaign = params.get("campaign");

  useEffect(() => {
    if (!campaign && !source) return;
    // Fire a single campaign_landing event so the reporting layer can attribute traffic.
    trackEvent("campaign_landing", { source, campaign });
    const targetId = CAMPAIGN_TARGETS[campaign];
    if (!targetId) return;
    // Poll for the target element — the homepage shows a LOADING state until
    // the featured data lands, so the section may not exist on first render.
    let attempts = 0;
    let cleared = false;
    const tick = () => {
      if (cleared) return;
      attempts += 1;
      const el = document.querySelector(`[data-testid="${targetId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.setAttribute("data-campaign-highlight", "true");
        setTimeout(() => el.removeAttribute("data-campaign-highlight"), 4000);
        return;
      }
      if (attempts < 25) setTimeout(tick, 200); // ~5s total budget
    };
    setTimeout(tick, 400);
    return () => { cleared = true; };
  }, [campaign, source]);

  return { source, campaign };
}
